import { useState, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Dialog, DialogBody, Typography } from '@material-tailwind/react'
import { use100vh } from 'react-div-100vh'
import { logger } from '../utils/helpers/logger'
import { useXp } from '../contexts/XpContext'
import { getExperienceDetails, getGeoXpConfig } from '../utils/helpers/supabase.helpers'
import { validateConfig } from '../utils/helpers/geoxp.helpers'
import { useMountEffect, usePageTitle } from '../hooks/toolkit'
import MapboxMap from '../Map/MapboxMap'
import Loading from '../layout/Loading'
import ManualSpot from '../components/ManualSpot'
import Center from '../components/Center'
import { useGeneralToast } from '../hooks/toast'
import PlayersCarousel from '../Map/PlayersCarousel'
import SinglePlayer from '../Map/SinglePlayer'

const SoundMap = ({ experienceId }: { experienceId: string }) => {
  usePageTitle('Experience | Agami')
  const height = use100vh()

  const { key } = useLocation()
  const autostart = key !== 'default'

  const toast = useGeneralToast()
  const { loadExperience, details, config, hasManualSpots, active, start, destroy, playManually } = useXp()

  const [loading, setLoading] = useState(true)
  const [startDialog, setStartDialog] = useState(!autostart)
  const [expError, setExpError] = useState(false)

  const getConfig = useCallback(async () => {
    setLoading(true)

    const { data: detailsData, error: detailsError } = await getExperienceDetails({ experienceId })
    const { data: configData, error: configError } = await getGeoXpConfig({ experienceId })

    if (detailsError || configError) {
      if (detailsError) logger.error(detailsError, 'LoadSoundMap')
      if (configError) logger.error(configError, 'LoadSoundMap')
      setExpError(true)
      setLoading(false)
      return
    }
    if (!detailsData || !configData) return

    const isValidConfig = validateConfig(configData)
    if (!isValidConfig) {
      return
    }

    loadExperience(configData, detailsData)
  }, [experienceId, loadExperience])

  const playSpotManually = useCallback(
    (code: string) => {
      const error = playManually(code)
      if (error) toast.error(`Cannot play manually, ${error}`)
    },
    [playManually, toast]
  )

  const handleStart = () => {
    setStartDialog(false)
    if (config) start()
  }

  useMountEffect(() => () => {
    destroy()
  })

  useEffect(() => {
    getConfig()
  }, [experienceId])

  useEffect(() => {
    if (config) {
      if (autostart || !startDialog) start()
      setLoading(false)
    }
  }, [config, autostart, startDialog])

  if (loading) return <Loading />

  if (expError) {
    return (
      <Center>
        <Typography variant="lead" className="text-white-yellow-500">
          Sorry, this map cannot be loaded :(
        </Typography>
      </Center>
    )
  }

  return (
    <>
      <div className="w-full z-0 relative" style={{ height: height ?? '100%' }}>
        <MapboxMap />

        {active?.length === 1 && <SinglePlayer />}

        {active?.length > 1 && <PlayersCarousel />}

        {active?.length === 0 && hasManualSpots && <ManualSpot onPlay={playSpotManually} />}
      </div>

      <Dialog
        open={startDialog}
        handler={() => {
          if (startDialog && config) start()
          setStartDialog((s) => !s)
        }}
        data-testid="experience-start-dialog"
      >
        <DialogBody className="p-4 text-center flex flex-col gap-y-4 text-primary">
          <Typography variant="h3">
            Welcome
            {!!details && ` to ${details.name}`}
          </Typography>
          <Typography>Are you ready to start the experience?</Typography>
          <Button onClick={handleStart} data-testid="experience-start-btn">
            Start
          </Button>
        </DialogBody>
      </Dialog>
    </>
  )
}

export default SoundMap
